import React, { useEffect, useRef, useState } from "react";

export type LpAnimatedBackgroundVariant = "auth" | "website" | "game";

interface LpAnimatedBackgroundProps {
  variant?: LpAnimatedBackgroundVariant;
}

type ParticleType = "star" | "ember";

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  phase: number;
  type: ParticleType;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return reduced;
}

function createParticles(width: number, height: number, variant: LpAnimatedBackgroundVariant): Particle[] {
  const particles: Particle[] = [];
  // game: densidade menor — roda junto com o Pixi e não pode competir por frame budget
  const density =
    variant === "game" ? 14000 : variant === "website" ? 8500 : 9000;
  const emberDensity =
    variant === "game" ? 36000 : variant === "website" ? 20000 : 22000;
  const starCap = variant === "game" ? 72 : variant === "website" ? 160 : 140;
  const emberCap = variant === "game" ? 18 : variant === "website" ? 42 : 36;
  const starCount = Math.min(starCap, Math.floor((width * height) / density));
  const emberCount = Math.min(emberCap, Math.floor((width * height) / emberDensity));

  for (let i = 0; i < starCount; i += 1) {
    particles.push({
      x: Math.random() * width,
      y: Math.random() * height * 0.82,
      size: Math.random() > 0.75 ? 3 : 2,
      speedX: 0,
      speedY: 0,
      opacity: 0.15 + Math.random() * 0.55,
      phase: Math.random() * Math.PI * 2,
      type: "star",
    });
  }

  for (let i = 0; i < emberCount; i += 1) {
    particles.push({
      x: Math.random() * width,
      y: height * (0.55 + Math.random() * 0.55),
      size: Math.random() > 0.6 ? 4 : 2,
      speedX: (Math.random() - 0.5) * 0.35,
      speedY: -0.35 - Math.random() * 0.75,
      opacity: 0.25 + Math.random() * 0.45,
      phase: Math.random() * Math.PI * 2,
      type: "ember",
    });
  }

  return particles;
}

function drawParticles(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  width: number,
  height: number,
  time: number,
  animate: boolean
) {
  ctx.clearRect(0, 0, width, height);

  for (const p of particles) {
    if (p.type === "star") {
      const twinkle = animate
        ? 0.45 + Math.sin(time * 0.002 + p.phase) * 0.35
        : 0.7;
      const alpha = p.opacity * twinkle;
      ctx.fillStyle = `rgba(220, 230, 255, ${alpha})`;
      ctx.fillRect(Math.floor(p.x), Math.floor(p.y), p.size, p.size);
      continue;
    }

    if (animate) {
      p.x += p.speedX;
      p.y += p.speedY;
      if (p.y < -8) {
        p.y = height + 6;
        p.x = Math.random() * width;
      }
    }

    const flicker = animate
      ? 0.6 + Math.sin(time * 0.004 + p.phase) * 0.25
      : 0.8;
    const alpha = p.opacity * flicker;
    ctx.fillStyle = `rgba(133, 178, 134, ${alpha})`;
    ctx.fillRect(Math.floor(p.x), Math.floor(p.y), p.size, p.size);
  }
}

const SKYLINE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 480 96" fill="none"><rect x="0" y="72" width="480" height="24" fill="#0a0d15"/><rect x="8" y="52" width="40" height="44" fill="#1b1f2b"/><rect x="14" y="58" width="6" height="6" fill="#85b286" opacity="0.55"/><rect x="26" y="66" width="6" height="6" fill="#a3c4a4" opacity="0.4"/><rect x="56" y="44" width="32" height="52" fill="#171a24"/><rect x="62" y="50" width="5" height="5" fill="#ffffff" opacity="0.2"/><rect x="74" y="58" width="5" height="5" fill="#85b286" opacity="0.45"/><rect x="96" y="60" width="48" height="36" fill="#1b1f2b"/><rect x="104" y="66" width="8" height="8" fill="#6b7385" opacity="0.35"/><rect x="120" y="74" width="8" height="8" fill="#85b286" opacity="0.5"/><rect x="152" y="36" width="28" height="60" fill="#141824"/><rect x="160" y="44" width="6" height="6" fill="#a3c4a4" opacity="0.5"/><rect x="172" y="56" width="6" height="6" fill="#ffffff" opacity="0.15"/><rect x="188" y="48" width="56" height="48" fill="#282d3d"/><rect x="196" y="56" width="8" height="8" fill="#85b286" opacity="0.4"/><rect x="212" y="64" width="8" height="8" fill="#85b286" opacity="0.35"/><rect x="252" y="56" width="36" height="40" fill="#1b1f2b"/><rect x="260" y="64" width="6" height="6" fill="#85b286" opacity="0.45"/><rect x="296" y="40" width="44" height="56" fill="#171a24"/><rect x="304" y="48" width="7" height="7" fill="#ffffff" opacity="0.18"/><rect x="320" y="60" width="7" height="7" fill="#a3c4a4" opacity="0.5"/><rect x="348" y="52" width="52" height="44" fill="#1b1f2b"/><rect x="356" y="60" width="8" height="8" fill="#6b7385" opacity="0.3"/><rect x="372" y="68" width="8" height="8" fill="#85b286" opacity="0.45"/><rect x="408" y="32" width="32" height="64" fill="#12151f"/><rect x="416" y="40" width="6" height="6" fill="#85b286" opacity="0.5"/><rect x="428" y="52" width="6" height="6" fill="#ffffff" opacity="0.12"/><rect x="448" y="56" width="32" height="40" fill="#282d3d"/><rect x="456" y="64" width="6" height="6" fill="#a3c4a4" opacity="0.4"/></svg>`;

const SKYLINE_PATTERN = `url("data:image/svg+xml,${encodeURIComponent(SKYLINE_SVG)}")`;

const skylineLayerStyle: React.CSSProperties = {
  backgroundImage: SKYLINE_PATTERN,
};

interface LpSkylineProps {
  placement: "viewport" | "footer";
}

export const LpSkyline: React.FC<LpSkylineProps> = ({ placement }) => {
  const reducedMotion = usePrefersReducedMotion();
  const staticClass = reducedMotion ? " lp-portal-bg-skyline--static" : "";

  const wrapClass =
    placement === "viewport"
      ? "lp-portal-bg-skyline-wrap lp-portal-bg-skyline-wrap--viewport"
      : "lp-portal-bg-skyline-wrap lp-portal-bg-skyline-wrap--footer";

  return (
    <div className={wrapClass} aria-hidden>
      <div
        className={`lp-portal-bg-skyline lp-portal-bg-skyline--far${staticClass}`}
        style={skylineLayerStyle}
      />
      <div
        className={`lp-portal-bg-skyline lp-portal-bg-skyline--near${staticClass}`}
        style={skylineLayerStyle}
      />
    </div>
  );
};

export const LpAnimatedBackground: React.FC<LpAnimatedBackgroundProps> = ({
  variant = "auth",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const frameRef = useRef<number>(0);
  const reducedMotion = usePrefersReducedMotion();

  const rootClass =
    variant === "website"
      ? "lp-portal-bg-root lp-portal-bg-root--fixed"
      : "lp-portal-bg-root lp-portal-bg-root--absolute";

  const atmosphereClass =
    variant === "website" || variant === "game"
      ? "lp-portal-bg-atmosphere lp-portal-bg-atmosphere--website"
      : "lp-portal-bg-atmosphere";

  const showPortal = variant === "auth";
  const showSkyline = variant === "auth";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const parentRect = parent.getBoundingClientRect();
      const viewportWidth =
        window.visualViewport?.width ?? document.documentElement.clientWidth;
      const viewportHeight =
        window.visualViewport?.height ?? document.documentElement.clientHeight;
      const width = parentRect.width > 1 ? parentRect.width : viewportWidth;
      const height = parentRect.height > 1 ? parentRect.height : viewportHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particlesRef.current = createParticles(width, height, variant);
      drawParticles(ctx, particlesRef.current, width, height, 0, !reducedMotion);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas.parentElement!);
    window.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("scroll", resize);

    if (reducedMotion) {
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", resize);
        window.visualViewport?.removeEventListener("resize", resize);
        window.visualViewport?.removeEventListener("scroll", resize);
      };
    }

    // ~30 FPS no canvas 2D em vez de 60 — decorativo, ninguém nota a
    // diferença, e corta pela metade o custo de main-thread por segundo. Em
    // salas (variant game) libera budget pro Pixi a 60; no site, corta TBT.
    const minFrameMs = variant === "game" || variant === "website" ? 33 : 0;
    let lastDraw = 0;

    const tick = (time: number) => {
      if (minFrameMs === 0 || time - lastDraw >= minFrameMs) {
        lastDraw = time;
        const { width, height } = canvas.getBoundingClientRect();
        drawParticles(ctx, particlesRef.current, width, height, time, true);
      }
      frameRef.current = requestAnimationFrame(tick);
    };

    const startTicking = () => {
      frameRef.current = requestAnimationFrame(tick);
    };

    // Site institucional (variant website): adia o início do loop até o
    // navegador ficar ocioso — sem isso, o canvas decorativo começa a
    // desenhar no mesmo instante em que a Home tenta pintar o hero, e os
    // dois competem por frame budget bem na janela que decide o LCP
    // (achado no Lighthouse mobile: esse script sozinho era o maior custo
    // de main-thread da página, maior que o próprio React). Um atraso
    // imperceptível no fundo decorativo troca por um LCP bem mais rápido.
    let idleHandle: number | null = null;
    let startTimeout: ReturnType<typeof setTimeout> | null = null;
    if (variant === "website") {
      const ric = window.requestIdleCallback;
      if (typeof ric === "function") {
        idleHandle = ric(startTicking, { timeout: 1500 });
      } else {
        startTimeout = setTimeout(startTicking, 200);
      }
    } else {
      startTicking();
    }

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("scroll", resize);
      cancelAnimationFrame(frameRef.current);
      if (idleHandle !== null) window.cancelIdleCallback?.(idleHandle);
      if (startTimeout !== null) clearTimeout(startTimeout);
    };
  }, [reducedMotion, variant]);

  const gridClass =
    variant === "website" || variant === "game"
      ? "lp-portal-bg-grid lp-portal-bg-grid--steady"
      : "lp-portal-bg-grid";

  const background = (
    <div className={rootClass} aria-hidden>
      <div className="lp-portal-bg-base" />
      {showPortal && (
        <div className="lp-portal-bg-portal lp-portal-bg-portal--center">
          <span className="lp-portal-bg-portal-core" />
          <span className="lp-portal-bg-portal-ring lp-portal-bg-portal-ring--1" />
          <span className="lp-portal-bg-portal-ring lp-portal-bg-portal-ring--2" />
          <span className="lp-portal-bg-portal-ring lp-portal-bg-portal-ring--3" />
        </div>
      )}
      <canvas ref={canvasRef} className="lp-portal-bg-canvas" />
      {showSkyline && <LpSkyline placement="viewport" />}
      <div className={atmosphereClass} />
      <div className={gridClass} />
    </div>
  );

  return background;
};

export default LpAnimatedBackground;
